import { IVec2d, IVec3d } from "./IChainInfo";

export interface IObj<T> {
    readonly position: T;
    readonly size: T;
}


export interface IObj3d extends IObj<IVec3d> {
}

export interface IObj2d extends IObj<IVec2d> {
}


export function isInside(vec: IVec3d, obj: IObj3d): boolean;
export function isInside(vec: IVec2d, obj: IObj2d): boolean
export function isInside(vec: IVec2d, obj: IObj2d): boolean {
    if (vec.x < obj.position.x)
        return false;
    if (vec.x > obj.position.x + obj.size.x)
        return false;

    if (vec.y < obj.position.y)
        return false;
    if (vec.y > obj.position.y + obj.size.y)
        return false;

    if (isIVec3d(vec) && isObj3d(obj)) {
        if (vec.z < obj.position.z)
            return false;
        if (vec.z > obj.position.z + obj.size.z)
            return false;
    }
    return true;

}

export function maxPos(obj: IObj3d): IVec3d;
export function maxPos(obj: IObj2d): IVec2d;
export function maxPos(obj: IObj3d | IObj2d) {
    return add(obj.position, obj.size);
}

export function topFrontRight(obj: IObj3d): IVec3d {
    return add(obj.position, obj.size);
}

export function topRight(obj: IObj2d): IVec2d {
    return add(obj.position, obj.size);
}

export function middle(obj: IObj3d): IVec3d {
    return {
        x: obj.position.x + (obj.size.x / 2),
        y: obj.position.y + (obj.size.y / 2),
        z: obj.position.z + (obj.size.z / 2)
    };
}

export function add(a: IVec3d, b: IVec3d): IVec3d;
export function add(a: IVec2d, b: IVec2d): IVec2d;
export function add(a: IVec2d | IVec3d, b: IVec2d | IVec3d): IVec2d | IVec3d {
    if (isIVec3d(a) && isIVec3d(b)) {
        return {
            x: a.x + b.x,
            y: a.y + b.y,
            z: a.z + b.z,
        };
    }
    return {
        x: a.x + b.x,
        y: a.y + b.y
    };
}
export function subtract(a: IVec3d, b: IVec3d): IVec3d;
export function subtract(a: IVec2d, b: IVec2d): IVec2d;
export function subtract(a: IVec2d | IVec3d, b: IVec2d | IVec3d): IVec2d | IVec3d {
    if (isIVec3d(a) && isIVec3d(b)) {
        return {
            x: a.x - b.x,
            y: a.y - b.y,
            z: a.z - b.z,
        };
    }
    return {
        x: a.x - b.x,
        y: a.y - b.y
    };
}

export function moveObj(obj: IObj2d, vec: IVec2d): IObj2d {
    return {
        size: { ...obj.size },
        position: add(obj.position, vec),
    };
}

export function isIVec2d(v: {}): v is IVec2d {
    const av = v as any;
    return (typeof av.x === "number")
        && (typeof av.y === "number");
}

export function isIVec3d(v: IVec2d | IVec3d): v is IVec3d {
    const av = v as any;
    return typeof av === "number";
}
export function isObj3d(o: IObj2d | IObj3d): o is IObj3d {
    return isIVec3d(o.size)
        && isIVec3d(o.position);
}

function intersectDim<T extends { [P in K]: number }, K extends keyof T>(
    x: IObj<T>,
    y: IObj<T>,
    dim: K
): boolean {
    for (let [a, b] of [[x, y], [y, x]]) {
        if (a.position[dim] + a.size[dim] <= b.position[dim])
            return false; //a is "left" of b
    }
    return true;
}


export function intersect(a: IObj3d, b: IObj3d): boolean;
export function intersect(a: IObj2d, b: IObj2d): boolean;
export function intersect(a: IObj2d, b: IObj2d): boolean {
    if (isObj3d(a) && isObj3d(b)) {
        const dimensions = ["x", "y", "z"] as (keyof IVec3d)[];
        return dimensions.every(dim => intersectDim(a, b, dim));
    }
    else {
        const dimensions = ["x", "y"] as (keyof IVec2d)[];
        return dimensions.every(dim => intersectDim(a, b, dim));
    }

}

export function cheapDist(a: IVec3d, b: IVec3d): number
export function cheapDist(a: IVec2d, b: IVec2d): number
export function cheapDist(a: IVec2d, b: IVec2d): number {
    var zDist = 0;
    if (isIVec3d(a) && isIVec3d(b)) {
        zDist = Math.abs(a.z - b.z);
    }
    return Math.abs(a.x - b.x) + Math.abs(a.y - b.y) + zDist;
}

/**
 * Given a list of rectangles, gives a rectangle containing all inputs
 * @param objs
 */
export function boundingBoxO2(...objs: IObj2d[]): IObj2d {
    const dims = ["x", "y"] as (keyof IVec2d)[];
    return boundingBoxN(objs, dims);
}
export function boundingBoxO3(...objs: IObj3d[]): IObj3d {
    const dims = ["x", "y", "z"] as (keyof IVec3d)[];
    return boundingBoxN(objs, dims);
}
export function boundingBoxV2(...points: IVec2d[]): IObj2d {
    let xMin = Number.POSITIVE_INFINITY;
    let xMax = Number.NEGATIVE_INFINITY;
    let yMin = Number.POSITIVE_INFINITY;
    let yMax = Number.NEGATIVE_INFINITY;
    for (let p of points) {
        xMin = Math.min(xMin, p.x);
        xMax = Math.max(xMax, p.x);
        yMin = Math.min(yMin, p.y);
        yMax = Math.max(yMax, p.y);
    }
    return {
        position: {
            x: xMin,
            y: yMin,
        },
        size: {
            x: xMax - xMin,
            y: yMax - yMin
        }
    };
}



function boundingBoxN<T extends { [P in K]: number }, K extends keyof T>(objs: IObj<T>[], dims: K[]) {
    if (objs.length === 0) {

        const result: any = {
            position: {},
            size: {}
        };
        for (let dim of dims) {
            result.position[dim] = 0;
            result.size[dim] = 0;
        }
        return result;
    }

    const result: IObj<T> = {
        position: { ...objs[0].position },
        size: { ...objs[0].size }
    }
    for (let obj of objs) {
        for (let dim of dims) {
            console.assert(obj.size[dim] >= 0, `Object size should be positive, but is negative for dimension ${dim}`, { dim, obj, objs });
            const oldMax = result.position[dim] + result.size[dim];
            const objMax = obj.position[dim] + obj.size[dim];
            const newMax = Math.max(oldMax, objMax);
            const newPos = Math.min(result.position[dim], obj.position[dim]);
            const newSize = newMax - newPos;
            console.assert(newSize >= 0, `Size should be >= 0, but is ${newSize} for dimension ${dim}`, { newSize, dim, obj, objs });
            (result.position[dim] as any) = newPos;
            (result.size[dim] as any) = newSize;
        }
    }
    return result;
}
